<template>
  <Modal name="newReport" v-bind="$attrs" :key='id'>
    <template v-slot:header>
        <h1 v-if="!editMode">{{ $t('create_schedule') }}</h1>
        <h1 v-else>{{ $t('edit_schedule') }}</h1>
    </template>
    <template v-slot:body>
        <div class="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-8">
            <div class="col-start-1 order-1 sm:order-2 row-start-2 sm:row-start-1">
                <div class="form">
                    <label>{{ $t('title') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_title')"></feather></label>
                    <input ref="name" type="text" name="name" v-model="title" maxlength="100" required />
                </div>
                <div class="form">
                    <label>{{ $t('trigger_date') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_trigger_date')"></feather></label>
                    <v-date-picker :attributes="calendarAttributes" v-model="triggerDate" mode="dateTime" :popover="{positionFixed: true}">
                        <template v-slot="{ inputValue, inputEvents }">
                            <input
                                :placeholder="$t('select')"
                                class=""
                                :value="inputValue"
                                v-on="inputEvents"
                            />
                        </template>
                    </v-date-picker>
                </div>
                <div class="form">
                    <label>{{ $t('repeats') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_repeats')"></feather></label>
                    <input type="checkbox" v-model="repeats" >
                </div>
                <div class="form" v-if="repeats">
                    <label>{{ $t('frequency') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_frequency')"></feather></label>
                    <multiselect :multiple="false" open-direction="bottom" :options="frequency.options" v-model="frequency.selected" :searchable="false" :close-on-select="true" :show-labels="false" :placeholder="$t('select')"></multiselect>
                </div>
                <div class="form" v-if="repeats">
                    <label>{{ $t('repeating_ends') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_repeating_ends')"></feather></label>
                    <multiselect :multiple="false" open-direction="bottom" v-model="ends.selected" :options="ends.options" :searchable="false" :close-on-select="true" :show-labels="false" :placeholder="$t('select')"></multiselect>
                </div>
                <div class="form flex items-center" v-if="ends.selected === 'After' && repeats">{{ $t('after') }} <input placeholder="Number" class="flex-grow mx-2" type="number" step="1" min="1" v-model.number="ends.after"> {{ $t('occurences') }}</div>
                <div class="form flex items-center" v-if="ends.selected === 'On' && repeats">{{ $t('on_date') }}
                    <v-date-picker :attributes="calendarAttributes" v-model="ends.on" :min-date="triggerDate" mode="dateTime" class="flex-grow ml-2" :popover="{positionFixed: true}">
                        <template v-slot="{ inputValue, inputEvents }">
                            <input
                                :placeholder="$t('select')"
                                class="flex-grow"
                                :value="inputValue"
                                v-on="inputEvents"
                            />
                        </template>
                    </v-date-picker>
                </div>
            </div>
            <div class="col-start-1 order-2 sm:order-1 sm:col-start-2 row-start-1">
                <div class="form">
                    <label>{{ $tc('report', 1) }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_report')"></feather></label>
                    <multiselect :multiple="false" open-direction="bottom" group-label="name" v-model="reportSelected" group-values="report" :options="reportsTypeList" label="name" track-by="id" :close-on-select="true" :show-labels="false" placeholder="Select"></multiselect>
                </div>
                <div class="form">
                    <label>{{ $tc('recipient', 2) }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_recipients_emails')"></feather></label>
                    <multiselect
                        @tag="addTag"
                        v-model="recipients.selected"
                        open-direction="bottom"
                        :multiple="true"
                        :taggable="true"
                        label="name"
                        track-by="email"
                        :options="recipients.list"
                        @search-change="findUsers"
                        :loading="loadingUsers"
                        :show-labels="false"
                        :tag-placeholder="$t('add_new')"
                        :placeholder="$t('search_add_recipient')"></multiselect>
                </div>
            </div>
        </div>
    </template>
    <template v-slot:footer>
        <div class="btn btn-primary submit" :class="!isValid ? 'disabled' : ''" @click="submit">
            <feather size="24px" type="send" /> {{ $t('submit') }}
        </div>
    </template>
</Modal>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Modal from '../modules/modals/components/Modal'

export default {
    name: 'modalNewSchedule',
    components: {
        Modal,
    },
    data(){
        return {
            id: null,
            triggerDate: null,
            title: '',
            repeats: false,
            loadingUsers: false,
            reportSelected: null,
            recipients: {
                selected:[],
                list: []
            },
            frequency: {
                selected: [],
                options: []
            },
            ends: {
                selected: null,
                options: [],
                after: null,
                on: null,
            },
        }
    },
    computed: {
        ...mapGetters([
            'newReport',
            'reportsTypeList',
            'users',
            'schedules',
            'calendarAttributes',
            'scheduleOptions',
        ]),
        editMode() {
            if(this.id){
                return true
            } else {
                return false
            }
        },
        isValid(){
            const minimumValidation = this.title.trim().length && this.reportSelected && this.triggerDate && this.recipients.selected.length;
            let result = false;
            if( !this.repeats ) {
                //if schedule is without repeatation
                result = minimumValidation
            } else {
                // if schedule is with repeatation

                // check frequency
                const freq = this.frequency.selected && this.frequency.selected.length

                // check repeat ends
                let end = this.ends.selected
                if(this.ends.selected === 'On') end = this.ends.selected && this.ends.on
                if(this.ends.selected === 'After') end = this.ends.selected && this.ends.after > 0
                result = minimumValidation && freq && end
            }
            return result
        }
    },
    methods: {
        ...mapMutations({
            setNewReportItem: 'setNewReportItem',
        }),
        ...mapActions({
            saveSchedule: 'saveSchedule',
            getUsers: 'getUsers',
        }),
        addTag (newTag) {
            function validateEmail(email) {
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(String(email).toLowerCase());
            }
            if(validateEmail(newTag)){
                const tag = {
                    name: newTag,
                    email: newTag
                }
                this.recipients.selected.push(tag)
                this.recipients.list.push(tag)
            } else {
                window.alert('You entered incorrect email format')
            }
        },
        submit(){
            if(this.isValid){
                let payload = {
                    id: this.id,
                    name: this.title,
                    recipients: JSON.stringify(this.recipients.selected.map(recipient => recipient.email)),
                    report_id: this.reportSelected.id,
                    repeat: this.repeats,
                }
                // SQL date format
                let date = this.triggerDate
                payload.date_from = typeof date === 'object' ? date.toISOString().slice(0,19).replace('T', ' ') : date
                //Frequency
                if(this.frequency.selected.length){
                    const index = this.frequency.options.findIndex(option => option === this.frequency.selected)
                    if(index !== -1){
                        payload.frequency = index
                    }
                } else {
                    payload.frequency = null
                }

                // Repeats end
                if (this.ends.selected === 'On'){
                    let endDate = this.ends.on
                    payload.repeat_end = JSON.stringify({date: typeof endDate === 'object' ? endDate.toISOString().slice(0,19).replace('T', ' ') : endDate})
                } else if (this.ends.selected === 'After'){
                    payload.repeat_end = JSON.stringify({number: this.ends.after})
                } else if (this.ends.selected === 'Never'){
                    payload.repeat_end = JSON.stringify({never: true})
                } else {
                    payload.repeat_end = JSON.stringify({})
                }
                this.saveSchedule(payload)
                this.$attrs.close()
            }
        },
        findUsers(query){
            this.loadingUsers = true
            this.getUsers(query).then(()=> {
                this.recipients.list = this.users
                this.loadingUsers = false
            })
        }
    },
    mounted() {
        this.findUsers('')
        this.frequency.options = this.scheduleOptions.frequency
        this.ends.options = this.scheduleOptions.ends
        this.$nextTick(()=>{
            this.$refs.name.focus()
        })
        if(this.$attrs.report){
            this.reportSelected = this.$attrs.report
        } else if(this.$attrs.id){
            //When id is provided update schedule details
            this.id = this.$attrs.id
            const schedule = this.schedules.find(schedule => schedule.id === this.$attrs.id)
            if (schedule){
                this.triggerDate = schedule.date_from != null ? new Date(schedule.date_from.replace(" ", "T") + 'Z') : null
                this.title = schedule.name
                this.repeats = schedule.repeat
                this.reportSelected = schedule.report
                //recipients: build tags
                JSON.parse(schedule.recipients).forEach(email => this.addTag(email))

                this.frequency.selected = schedule.frequency !== null ? this.frequency.options[schedule.frequency] : []
                const repeatType = Object.keys(JSON.parse(schedule.repeat_end))[0]
                const repeatValue = Object.values(JSON.parse(schedule.repeat_end))[0]
                switch(repeatType){
                    case 'date':
                        this.ends.selected = 'On'
                        this.ends.on = repeatValue != null ? new Date(repeatValue.replace(" ", "T") + 'Z') : null
                        break;
                    case 'number':
                        this.ends.selected = 'After'
                        this.ends.after = repeatValue
                        break;
                    case 'never':
                        this.ends.selected = 'Never'
                        break;
                }
            }
        }
    }
}
</script>

<style lang="sass" scoped>
h1
    font-size: 25px!important
label
    font-weight: 700
    font-size: 17px
    margin-bottom: 4px!important
input, select, textarea
    border: 1px solid #F0F1F3!important
    border-radius: 7px
    width: 100%
input[type='checkbox']
    min-width: 25px
    width: initial!important
    height: 25px
    min-height: initial!important
.form
    margin-bottom: 1rem
</style>
